import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/content';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className={`ri-movie-2-fill text-2xl ${isScrolled || !isHome ? 'text-primary-500' : 'text-background-50'}`}></i>
            </div>
            <span className={`font-heading font-bold text-lg hidden sm:block ${isScrolled || !isHome ? 'text-foreground-950' : 'text-background-50'}`}>
              日韩影视导航
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <NavLink to="/" current={location.pathname} isScrolled={isScrolled} isHome={isHome}>
              首页
            </NavLink>
            {categories.map((cat) => (
              <NavLink
                key={cat.slug}
                to={`/category/${cat.slug}`}
                current={location.pathname}
                isScrolled={isScrolled}
                isHome={isHome}
              >
                {cat.name}
              </NavLink>
            ))}
            <NavLink to="/about" current={location.pathname} isScrolled={isScrolled} isHome={isHome}>
              关于我们
            </NavLink>
          </div>

          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg ${
              isScrolled || !isHome ? 'text-foreground-950' : 'text-background-50'
            }`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="菜单"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-3 flex flex-col gap-1">
            <MobileNavLink to="/">首页</MobileNavLink>
            {categories.map((cat) => (
              <MobileNavLink key={cat.slug} to={`/category/${cat.slug}`}>
                {cat.name}
              </MobileNavLink>
            ))}
            <MobileNavLink to="/about">关于我们</MobileNavLink>
          </div>
        </div>
      )}
    </nav>
  );
}

function NavLink({
  to,
  children,
  current,
  isScrolled,
  isHome,
}: {
  to: string;
  children: string;
  current: string;
  isScrolled: boolean;
  isHome: boolean;
}) {
  const isActive = current === to || (to !== '/' && current.startsWith(to));
  const textColor = isScrolled || !isHome ? 'text-foreground-700' : 'text-background-50/80';
  const activeColor = isScrolled || !isHome ? 'text-primary-500' : 'text-background-50';
  const hoverBg = isScrolled || !isHome ? 'hover:bg-background-100' : 'hover:bg-background-50/15';

  return (
    <Link
      to={to}
      className={`whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${hoverBg} ${
        isActive ? activeColor + ' font-semibold' : textColor
      }`}
    >
      {children}
    </Link>
  );
}

function MobileNavLink({ to, children }: { to: string; children: string }) {
  return (
    <Link
      to={to}
      className="block px-4 py-3 rounded-lg text-foreground-700 hover:bg-background-100 text-sm font-medium transition-colors cursor-pointer"
    >
      {children}
    </Link>
  );
}